import { useEffect, useState } from "react";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Text,
} from "@shopify/polaris";
import { publicationRequest } from "../../../../packages/shared/stock-publication/client";
import type { QueueAccount } from "../../../../packages/shared/shopify-queue/client";
const base = import.meta.env.VITE_API_URL || "https://api.whenlyapp.com";
type Summary = {
  id: string;
  actor_id: string;
  product_count: number;
  new_job_count: number;
  created_at: string;
  store_domain: string | null;
};
type List = { requests: Summary[]; nextCursor: number | null; total: number };
type Detail = {
  request: Summary;
  products: {
    product_id: number;
    job_id: string;
    sku: string | null;
    title: string | null;
    status: string | null;
    error: string | null;
    unverified: boolean;
  }[];
  counts: Record<string, number>;
  nextCursor: number | null;
};
export function PublicationHistory({
  account,
  refresh,
}: {
  account: QueueAccount;
  refresh: number;
}) {
  const [pages, setPages] = useState<number[]>([]),
    [data, setData] = useState<List | null>(null),
    [error, setError] = useState(""),
    [selected, setSelected] = useState<string | null>(null),
    [reload, setReload] = useState(0);
  const cursor = pages[pages.length - 1];
  useEffect(() => {
    const abort = new AbortController();
    setData(null);
    setError("");
    void publicationRequest<List>(
      base,
      account,
      `/requests${cursor ? "?cursor=" + cursor : ""}`,
      { signal: abort.signal },
    )
      .then((value) => {
        if (!abort.signal.aborted) setData(value);
      })
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, [account, cursor, refresh, reload]);
  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Stock publication receipts
        </Text>
        <Button
          onClick={() => {
            setPages([]);
            setReload((v) => v + 1);
          }}
        >
          Refresh receipts
        </Button>
        {error && <Banner tone="critical">{error}</Banner>}
        {!data && !error && <Text as="p">Loading receipts…</Text>}
        {data && <Text as="p">{data.total} saved publications.</Text>}
        {data?.requests.map((row) => (
          <BlockStack gap="200" key={row.id}>
            <Text as="p">Shopify store: {row.store_domain || "Not recorded on this older receipt"}</Text>
            <Text as="p">
              {row.product_count} products queued on {row.created_at} UTC by
              staff #{row.actor_id}. {row.new_job_count} new updates;{" "}
              {row.product_count - row.new_job_count} joined waiting updates.
            </Text>
            <Button
              onClick={() => setSelected(selected === row.id ? null : row.id)}
            >{`View receipt ${row.id}`}</Button>
            {selected === row.id && (
              <PublicationDetail
                key={row.id + reload}
                id={row.id}
                account={account}
              />
            )}
          </BlockStack>
        ))}
        <InlineStack gap="200">
          {pages.length > 0 && (
            <Button onClick={() => setPages((p) => p.slice(0, -1))}>
              Previous receipts
            </Button>
          )}
          {data?.nextCursor && (
            <Button onClick={() => setPages((p) => [...p, data.nextCursor!])}>
              Older receipts
            </Button>
          )}
        </InlineStack>
      </BlockStack>
    </Card>
  );
}
export function PublicationDetail({
  id,
  account,
}: {
  id: string;
  account: QueueAccount;
}) {
  const [pages, setPages] = useState<number[]>([]),
    [data, setData] = useState<Detail | null>(null),
    [error, setError] = useState("");
  const cursor = pages[pages.length - 1];
  useEffect(() => {
    const abort = new AbortController();
    setData(null);
    setError("");
    void publicationRequest<Detail>(
      base,
      account,
      `/requests/${encodeURIComponent(id)}${cursor ? "?cursor=" + cursor : ""}`,
      { signal: abort.signal },
    )
      .then((value) => {
        if (!abort.signal.aborted) setData(value);
      })
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, [account, id, cursor]);
  return (
    <BlockStack gap="200">
      {error && <Banner tone="critical">{error}</Banner>}
      {!data && !error && <Text as="p">Loading publication results…</Text>}
      {data && (
        <Text as="p">
          Shopify store: {data.request.store_domain || "Not recorded on this older receipt"}.{" "}
          {Object.entries(data.counts)
            .map(([status, count]) => `${status}: ${count}`)
            .join(" · ")}
        </Text>
      )}
      {data?.products.map((row) => (
        <BlockStack gap="100" key={row.product_id}>
          <Text as="p">
            {row.sku || "#" + row.product_id} ·{" "}
            {row.title || "Product no longer present"}:{" "}
            {row.unverified
              ? "Older success, unverified"
              : row.status || "Job missing, needs review"}
          </Text>
          {row.error && (
            <Text as="p" tone="critical">
              {row.error}
            </Text>
          )}
        </BlockStack>
      ))}
      <InlineStack gap="200">
        {pages.length > 0 && (
          <Button onClick={() => setPages((p) => p.slice(0, -1))}>
            Previous results
          </Button>
        )}
        {data?.nextCursor && (
          <Button onClick={() => setPages((p) => [...p, data.nextCursor!])}>
            More results
          </Button>
        )}
      </InlineStack>
    </BlockStack>
  );
}
